import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Package, Building, Calendar, TrendingUp } from 'lucide-react';
import { AllScannedProduct } from './StaffAllScannedItemsView';

interface StaffAllScannedItemsStatsProps {
  products: AllScannedProduct[];
}

export const StaffAllScannedItemsStats = ({ products }: StaffAllScannedItemsStatsProps) => {
  // Calculate stats
  const totalProducts = products.length;
  const uniqueSuppliers = new Set(products.map(p => p.orderSupplier)).size;
  const uniqueOrders = new Set(products.map(p => p.orderId)).size;
  const newProducts = products.filter(p => p.posStatus !== 'found').length;
  const productsWithCredit = products.filter(p => p.hasCredit).length;
  
  // Calculate recent activity (last 7 days)
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const recentScans = products.filter(p => new Date(p.createdAt) > sevenDaysAgo).length;

  const stats = [
    {
      label: 'Total Scanned',
      value: totalProducts.toLocaleString(),
      icon: Package,
      color: 'text-primary'
    },
    {
      label: 'Suppliers',
      value: uniqueSuppliers.toString(),
      icon: Building,
      color: 'text-blue-600'
    },
    {
      label: 'Orders',
      value: uniqueOrders.toString(),
      icon: Calendar,
      color: 'text-green-600'
    },
    {
      label: 'Recent (7d)',
      value: recentScans.toString(),
      icon: TrendingUp,
      color: 'text-orange-600'
    }
  ];

  const additionalStats = [
    {
      label: 'New Products',
      value: `${newProducts} (${totalProducts > 0 ? Math.round((newProducts / totalProducts) * 100) : 0}%)`,
      description: 'Products not found in existing system'
    },
    {
      label: 'Credit Items',
      value: `${productsWithCredit} (${totalProducts > 0 ? Math.round((productsWithCredit / totalProducts) * 100) : 0}%)`,
      description: 'Products with credit issues'
    }
  ];

  if (totalProducts === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4 md:mb-6">
      {stats.map((stat, index) => {
        const IconComponent = stat.icon;
        return (
          <Card key={index} className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-muted/30`}>
                  <IconComponent className={`w-4 h-4 ${stat.color}`} />
                </div>
                <div className="min-w-0">
                  <div className="text-lg font-bold text-foreground truncate">
                    {stat.value}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {stat.label}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      
      {/* Additional stats in a separate row on mobile */}
      <div className="col-span-2 lg:col-span-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {additionalStats.map((stat, index) => (
                <div key={index} className="text-center sm:text-left">
                  <div className="text-sm font-medium text-foreground">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                  <div className="text-xs text-muted-foreground/80 mt-1">{stat.description}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};